/**
 * HTTP API クライアント。
 *
 * 方針:
 * - 例外は `ApiError` に統一し、呼び出し側が `code` で分岐できるようにする。
 * - 401 は「セッション切れ」として上位（AuthGate）が拾えるよう専用のコードで投げる。
 *   ここで勝手に画面遷移させない（保存中のリクエストが黙って消えるのを避けるため）。
 * - ネットワーク障害は `offline` コードとして区別する。送信キューが再試行の可否を
 *   判断するのに使う（docs/02-architecture.md §5 層2）。
 */

import { SESSION_ID } from '@app/storage/session-id';
import type {
  ApiErrorBody,
  ApiErrorCode,
  Category,
  ListCategoriesResponse,
  ListRevisionsResponse,
  CreateNoteRequest,
  CreateRevisionRequest,
  ExportManifest,
  ExtractRequest,
  ExtractResponse,
  ImageMeta,
  ListNotesQuery,
  ListNotesResponse,
  Note,
  Revision,
  RevisionMeta,
  SearchQuery,
  SearchResponse,
  SessionInfo,
  UpdateNoteRequest,
  UpdateNoteResponse,
} from '@shared/types';

export class ApiError extends Error {
  readonly code: ApiErrorCode | 'offline';
  readonly status: number;

  constructor(code: ApiErrorCode | 'offline', message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }

  /** 再試行して回復し得るか。送信キューのバックオフ判断に使う。 */
  get retryable(): boolean {
    if (this.code === 'offline') return true;
    // 5xx は一時障害の可能性がある。4xx は再送しても同じ結果になる。
    return this.status >= 500;
  }

  /** セッション切れ。再ログインが必要。 */
  get isUnauthorized(): boolean {
    return this.code === 'unauthorized' || this.status === 401;
  }
}

async function request<T>(
  method: string,
  path: string,
  options: { body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: options.body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError('offline', '通信できませんでした', 0);
  }

  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    let code: ApiErrorCode = res.status === 401 ? 'unauthorized' : 'internal';
    let message = `リクエストが失敗しました (${res.status})`;
    try {
      const parsed = (await res.json()) as Partial<ApiErrorBody>;
      if (parsed.error) {
        code = parsed.error.code;
        message = parsed.error.message;
      }
    } catch {
      // 本文が JSON でない場合は既定のメッセージのまま
    }
    throw new ApiError(code, message, res.status);
  }

  return (await res.json()) as T;
}

function toQuery(params: Record<string, string | number | boolean | undefined>): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    sp.set(key, String(value));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const api = {
  // --- 認証 ---
  getSession: (signal?: AbortSignal) =>
    request<SessionInfo>('GET', '/api/auth/session', signal ? { signal } : {}),
  logout: () => request<void>('POST', '/api/auth/logout'),
  /** ログインはリダイレクトを伴うため fetch ではなく location 遷移で行う。 */
  loginUrl: () => '/api/auth/login',
  /** 実行環境（本番／プレビュー）。認証前に取得できる。 */
  getMeta: (signal?: AbortSignal) =>
    request<{ environment: 'production' | 'preview' }>(
      'GET',
      '/api/meta',
      signal ? { signal } : {},
    ),
  /**
   * プレビュー限定のトークンログイン。
   * プレビュー URL はバージョンごとに変わり Google のリダイレクト URI を登録できないため
   * （ADR-029）。本番では常に 403 になる。
   */
  previewLogin: (token: string) =>
    request<{ authenticated: true; email: string }>('POST', '/api/auth/preview-login', {
      body: { token },
    }),

  // --- ノート ---
  listNotes: (query: ListNotesQuery = {}, signal?: AbortSignal) =>
    request<ListNotesResponse>('GET', `/api/notes${toQuery({ ...query })}`, signal ? { signal } : {}),
  getNote: (id: string, signal?: AbortSignal) =>
    request<Note>('GET', `/api/notes/${encodeURIComponent(id)}`, signal ? { signal } : {}),
  createNote: (body: CreateNoteRequest) => request<Note>('POST', '/api/notes', { body }),
  updateNote: (id: string, body: UpdateNoteRequest, signal?: AbortSignal) =>
    request<UpdateNoteResponse>('PUT', `/api/notes/${encodeURIComponent(id)}`, {
      body,
      ...(signal ? { signal } : {}),
    }),
  trashNote: (id: string) => request<void>('DELETE', `/api/notes/${encodeURIComponent(id)}`),
  restoreNote: (id: string) =>
    request<Note>('POST', `/api/notes/${encodeURIComponent(id)}/restore`),
  purgeNote: (id: string) => request<void>('DELETE', `/api/notes/${encodeURIComponent(id)}/purge`),
  extractNote: (id: string, body: ExtractRequest) =>
    request<ExtractResponse>('POST', `/api/notes/${encodeURIComponent(id)}/extract`, { body }),

  // --- リビジョン ---
  listRevisions: async (noteId: string): Promise<RevisionMeta[]> =>
    (
      await request<ListRevisionsResponse>(
        'GET',
        `/api/notes/${encodeURIComponent(noteId)}/revisions`,
      )
    ).revisions,
  getRevision: (revisionId: number) => request<Revision>('GET', `/api/revisions/${revisionId}`),
  createRevision: (noteId: string, body: CreateRevisionRequest) =>
    request<RevisionMeta>('POST', `/api/notes/${encodeURIComponent(noteId)}/revisions`, { body }),
  /** 復元も通常の保存と同じ応答を返す（実行前に destructive リビジョンが切られている）。 */
  revertNote: (noteId: string, revisionId: number) =>
    request<UpdateNoteResponse>('POST', `/api/notes/${encodeURIComponent(noteId)}/revert`, {
      body: { revision_id: revisionId, session_id: SESSION_ID },
    }),

  // --- カテゴリ ---
  listCategories: async (): Promise<Category[]> =>
    (await request<ListCategoriesResponse>('GET', '/api/categories')).categories,
  createCategory: (name: string) => request<Category>('POST', '/api/categories', { body: { name } }),
  updateCategory: (id: number, patch: { name?: string; sort_order?: number }) =>
    request<Category>('PUT', `/api/categories/${id}`, { body: patch }),
  /** 並べ替え。指定した ID の順序がそのまま sort_order になる。 */
  reorderCategories: async (order: number[]): Promise<Category[]> =>
    (await request<ListCategoriesResponse>('PUT', '/api/categories', { body: { order } })).categories,

  // --- 検索 ---
  search: (query: SearchQuery, signal?: AbortSignal) =>
    request<SearchResponse>('GET', `/api/search${toQuery({ ...query })}`, signal ? { signal } : {}),

  // --- 画像 ---
  /** 画像は multipart のため request() を通さない。 */
  async uploadImage(file: Blob, noteId: string | null): Promise<ImageMeta> {
    const form = new FormData();
    form.set('file', file);
    if (noteId) form.set('note_id', noteId);
    let res: Response;
    try {
      res = await fetch('/api/images', {
        method: 'POST',
        credentials: 'same-origin',
        body: form,
      });
    } catch {
      throw new ApiError('offline', '通信できませんでした', 0);
    }
    if (!res.ok) {
      let code: ApiErrorCode = res.status === 401 ? 'unauthorized' : 'internal';
      let message = `画像のアップロードに失敗しました (${res.status})`;
      try {
        const parsed = (await res.json()) as Partial<ApiErrorBody>;
        if (parsed.error) {
          code = parsed.error.code;
          message = parsed.error.message;
        }
      } catch {
        /* 既定のメッセージ */
      }
      throw new ApiError(code, message, res.status);
    }
    return (await res.json()) as ImageMeta;
  },

  // --- エクスポート ---
  exportManifest: () => request<ExportManifest>('GET', '/api/export/manifest'),
};
